import React, { useState, useEffect, useRef } from 'react'
import { Icon, List, Checkbox } from 'semantic-ui-react'
import './MultiSelectDropdown.scss'

interface MultiSelectDropdownProps {
  options: { key: string; text: string; value: string }[]
  placeholder: string
  value: string[]
  onChange: (event: React.SyntheticEvent, data: any) => void
  disabled?: boolean
  fluid?: boolean
  style?: React.CSSProperties
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  options,
  placeholder,
  value,
  onChange,
  disabled = false,
  fluid = false,
  style = {},
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  // Handle checkbox toggle
  const handleToggle = (optionValue: string) => {
    const newValue = value.includes(optionValue) ? value.filter((v) => v !== optionValue) : [...value, optionValue]

    onChange({} as React.SyntheticEvent, { value: newValue })
  }

  // Generate display text (shows count instead of tags)
  const getDisplayText = () => {
    if (value.length === 0) {
      return placeholder
    }

    // Show "Select columns here" or a count of selected items
    return `${value.length} column${value.length !== 1 ? 's' : ''} selected`
  }

  return (
    <div
      ref={dropdownRef}
      className={`multi-select-dropdown ${fluid ? 'fluid' : ''} ${disabled ? 'disabled' : ''}`}
      style={style}
    >
      <div className="dropdown-trigger" onClick={() => !disabled && setIsOpen(!isOpen)}>
        <div className="dropdown-text">
          <span className={value.length === 0 ? 'placeholder-text' : ''}>{getDisplayText()}</span>
        </div>
        <Icon name={isOpen ? 'angle up' : 'angle down'} />
      </div>

      {isOpen && !disabled && (
        <div className="dropdown-menu">
          <List divided relaxed>
            {options.map((option) => (
              <List.Item key={option.key} className="dropdown-item">
                <Checkbox
                  label={option.text}
                  checked={value.includes(option.value)}
                  onChange={() => handleToggle(option.value)}
                />
              </List.Item>
            ))}
          </List>
        </div>
      )}
    </div>
  )
}

export default MultiSelectDropdown
